"use client"

import * as React from "react"
import type { Control, UseFormReturn } from "react-hook-form"

import { useTranslations } from "@/i18n"
import { coreTextLimits } from "@ibada/shared-types/common"
import { DOCUMENT_TIMEZONE_VALUES } from "@ibada/shared-types/workspace"

import { type BranchFormInput } from "@/features/church/config/branches/schemas/branch-form.schema"
import { BranchStatus } from "@/features/church/config/branches/types/branch.types"
import { MemberAsyncSelect } from "@/shared/components/member-async-select"
import {
  Form,
  FormControl,
  FormControlledField,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormSelectField,
  Input,
  PhoneInput,
  Switch,
  Textarea,
} from "@ibada/ui"
import { MobileMemberSelectRow } from "@/shared/components/mobile-member-select-row"
import {
  MobileFormCheckRow,
  MobileFormError,
  MobileFormInputRow,
  MobileFormPhoneRow,
  MobileFormRow,
  MobileFormSection,
  MobileFormSelectRow,
  MobileFormTextArea,
} from "@/shared/components/mobile-form"

const BRANCH_STATUS_OPTIONS = [
  { value: BranchStatus.ACTIVE, label: "active" },
  { value: BranchStatus.INACTIVE, label: "inactive" },
] as const

type BranchFormFieldsProps = {
  control: Control<BranchFormInput>
  /** `create` hides the principal picker + status (no members yet, status is implicit ACTIVE). */
  mode: "create" | "edit"
  /** Resolved label for the principal-member field (may come from a custom-field template). */
  principalLabel: string
  /** Show the status select. Hidden where status is managed elsewhere (e.g. a Danger Zone). */
  showStatus?: boolean
}

/**
 * The branch create/edit form fields (name, code, details, phone, principal, language, timezone, status).
 * Shared by {@link BranchFormModal} (dialog) and the inline Branch Details → Settings editor so the field set
 * stays in one place. The caller owns the RHF instance, submit, and layout wrapper.
 */
export function BranchFormFields({ control, mode, principalLabel, showStatus = true }: BranchFormFieldsProps) {
  const t = useTranslations("churchConfig.branches.form")

  return (
    <>
      <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3'>
        <FormControlledField
          control={control}
          name='name'
          className='min-w-0 flex-1'
          label={t("fields.name")}
          renderInput={({ field }) => (
            <Input
              {...field}
              maxLength={coreTextLimits.branchName}
              showCount
              data-testid='branch-form-name-input'
              placeholder={t("fields.namePlaceholder")}
            />
          )}
        />

        <FormControlledField
          control={control}
          name='code'
          className='w-full sm:w-32'
          label={t("fields.code")}
          renderInput={({ field }) => (
            <Input
              {...field}
              data-testid='branch-form-code-input'
              value={field.value ?? ""}
              placeholder={t("fields.codePlaceholder")}
              disabled
              readOnly
              maxLength={coreTextLimits.branchCode}
              onChange={(event) =>
                field.onChange(event.target.value.toUpperCase().slice(0, coreTextLimits.branchCode))
              }
            />
          )}
        />
      </div>

      <FormControlledField
        control={control}
        name='details'
        label={t("fields.details")}
        renderInput={({ field }) => (
          <Textarea
            {...field}
            data-testid='branch-form-details-input'
            placeholder={t("fields.detailsPlaceholder")}
            maxLength={coreTextLimits.branchDescription}
            showCount
            rows={4}
          />
        )}
      />

      <FormControlledField
        control={control}
        name='phoneNumber'
        label={t("fields.phoneNumber")}
        renderInput={({ field }) => (
          <PhoneInput
            data-testid='branch-form-phone-input'
            value={field.value ?? ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={t("fields.phoneNumberPlaceholder")}
          />
        )}
      />

      {/* Principal member is only meaningful once members exist on the branch — hidden at create time. */}
      {mode === "edit" ? (
        <FormControlledField
          control={control}
          name='principalMemberId'
          label={principalLabel}
          description={t("fields.principalHint")}
          renderInput={({ field }) => (
            <MemberAsyncSelect
              value={field.value ?? ""}
              onValueChange={field.onChange}
              placeholder={t("fields.principalPlaceholder")}
              searchPlaceholder={t("fields.principalSearchPlaceholder")}
              clearable
              id='branch-form-principal-trigger'
            />
          )}
        />
      ) : null}

      {/* Official document language + timezone (inherited from the workspace, overridable per branch). */}
      <div className='grid gap-4 sm:grid-cols-2'>
        <FormSelectField
          control={control}
          name='language'
          label={t("fields.language")}
          description={t("fields.languageHelper")}
          options={[
            { value: "fr", label: t("fields.languageFr") },
            { value: "en", label: t("fields.languageEn") },
            { value: "rw", label: t("fields.languageRw") },
          ]}
          selectProps={{ id: "branch-form-language-trigger" }}
        />
        <FormSelectField
          control={control}
          name='timezone'
          label={t("fields.timezone")}
          description={t("fields.timezoneHelper")}
          options={DOCUMENT_TIMEZONE_VALUES.map((value) => ({
            value,
            label: value.replace(/_/g, " "),
          }))}
          selectProps={{ id: "branch-form-timezone-trigger" }}
        />
      </div>

      {/*
        Join the branch's administrators — not become its only one, and not a side effect of creating it.
        A branch is a whole organisation: its administrators receive its notifications and approvals, and
        under BRANCH_AUTONOMY see its figures. Whoever should run it is invited to it, which is how a branch
        normally gets its people, so this stays off unless the creator asks to be one of them.
      */}
      {mode === "create" ? (
        <FormField
          control={control}
          name='grantCreatorAccess'
          render={({ field }) => (
            <FormItem className='bg-muted/40 flex flex-row items-center justify-between gap-4 space-y-0 rounded-lg border p-4'>
              <div className='min-w-0 space-y-1'>
                <FormLabel className='font-medium'>{t("fields.grantCreatorAccess")}</FormLabel>
                <FormDescription>{t("fields.grantCreatorAccessHelper")}</FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                  aria-label={t("fields.grantCreatorAccess")}
                />
              </FormControl>
            </FormItem>
          )}
        />
      ) : null}

      {mode === "edit" && showStatus ? (
        <FormSelectField
          control={control}
          name='status'
          label={t("fields.status")}
          options={BRANCH_STATUS_OPTIONS.map((option) => ({
            value: option.value,
            label: option.value === BranchStatus.ACTIVE ? t("fields.statusActive") : t("fields.statusInactive"),
          }))}
          selectProps={{ id: "branch-form-status-trigger" }}
        />
      ) : null}
    </>
  )
}

type BranchMobileFormFieldsProps = Omit<BranchFormFieldsProps, "control"> & {
  form: UseFormReturn<BranchFormInput>
  /** The principal's name when it is already known (an edit), so the row never shows a raw id. */
  principalName?: string
  disabled?: boolean
}

/**
 * The same field set as {@link BranchFormFields}, as the phone kit's grouped rows: language, timezone, principal
 * and status open picker sheets, so the caller renders these on a full screen (never inside a sheet).
 */
export function BranchMobileFormFields({
  form,
  mode,
  principalLabel,
  principalName,
  showStatus = true,
  disabled = false,
}: BranchMobileFormFieldsProps) {
  const t = useTranslations("churchConfig.branches.form")
  const tMobile = useTranslations("common.mobileForm")
  const values = form.watch()
  const errors = form.formState.errors
  const picker = { doneLabel: tMobile("done"), emptyLabel: tMobile("noResults"), disabled }
  const set = <TName extends keyof BranchFormInput>(name: TName, value: BranchFormInput[TName]) =>
    form.setValue(name, value as never, {
      shouldDirty: true,
      shouldValidate: form.formState.isSubmitted || Boolean(errors[name]),
    })

  return (
    <>
      <div>
        <MobileFormSection>
          <MobileFormInputRow
            label={t("fields.name")}
            value={values.name ?? ""}
            onChange={(event) => set("name", event.target.value)}
            placeholder={t("fields.namePlaceholder")}
            maxLength={coreTextLimits.branchName}
            disabled={disabled}
            invalid={Boolean(errors.name)}
          />
          <MobileFormRow label={t("fields.code")} value={values.code} placeholder={t("fields.codePlaceholder")} />
          {mode === "edit" ? (
            <MobileMemberSelectRow
              {...picker}
              label={principalLabel}
              placeholder={t("fields.principalPlaceholder")}
              searchPlaceholder={t("fields.principalSearchPlaceholder")}
              value={values.principalMemberId || undefined}
              onChange={(value) => set("principalMemberId", value ?? "")}
              clearLabel={tMobile("none")}
              selectedLabel={principalName}
              invalid={Boolean(errors.principalMemberId)}
            />
          ) : null}
          {mode === "edit" && showStatus ? (
            <MobileFormSelectRow
              {...picker}
              label={t("fields.status")}
              placeholder={tMobile("choose")}
              value={values.status}
              onChange={(value) => value && set("status", value as BranchStatus)}
              options={BRANCH_STATUS_OPTIONS.map((option) => ({
                value: option.value,
                label: option.value === BranchStatus.ACTIVE ? t("fields.statusActive") : t("fields.statusInactive"),
              }))}
            />
          ) : null}
        </MobileFormSection>
        <MobileFormError message={errors.name?.message ?? errors.code?.message ?? errors.principalMemberId?.message} />
      </div>
      <div>
        <MobileFormSection title={t("fields.phoneNumber")}>
          <MobileFormPhoneRow
            value={values.phoneNumber ?? ""}
            onChange={(value) => set("phoneNumber", value)}
            placeholder={t("fields.phoneNumberPlaceholder")}
            disabled={disabled}
          />
        </MobileFormSection>
        <MobileFormError message={errors.phoneNumber?.message} />
      </div>
      <MobileFormSection title={t("fields.details")}>
        <MobileFormTextArea
          value={values.details ?? ""}
          onChange={(event) => set("details", event.target.value)}
          placeholder={t("fields.detailsPlaceholder")}
          maxLength={coreTextLimits.branchDescription}
          disabled={disabled}
          aria-label={t("fields.details")}
        />
      </MobileFormSection>
      <MobileFormSection footer={t("fields.timezoneHelper")}>
        <MobileFormSelectRow
          {...picker}
          label={t("fields.language")}
          placeholder={tMobile("choose")}
          value={values.language}
          onChange={(value) => value && set("language", value as BranchFormInput["language"])}
          options={[
            { value: "fr", label: t("fields.languageFr") },
            { value: "en", label: t("fields.languageEn") },
            { value: "rw", label: t("fields.languageRw") },
          ]}
        />
        <MobileFormSelectRow
          {...picker}
          label={t("fields.timezone")}
          placeholder={tMobile("choose")}
          value={values.timezone}
          onChange={(value) => value && set("timezone", value as BranchFormInput["timezone"])}
          options={DOCUMENT_TIMEZONE_VALUES.map((value) => ({ value, label: value.replace(/_/g, " ") }))}
        />
      </MobileFormSection>
      {mode === "create" ? (
        <MobileFormSection>
          <MobileFormCheckRow
            label={t("fields.grantCreatorAccess")}
            description={t("fields.grantCreatorAccessHelper")}
            checked={Boolean(values.grantCreatorAccess)}
            onToggle={() => set("grantCreatorAccess", !values.grantCreatorAccess)}
            disabled={disabled}
          />
        </MobileFormSection>
      ) : null}
      <MobileFormError message={errors.root?.serverError?.message} />
    </>
  )
}

/** Re-export so callers can wrap fields in the shared {@link Form} provider without a second import. */
export { Form }
